// Onglet superAdmin « Cartes » : dépenses des cartes Qonto comparées sur la
// période globale. Le menu ne l'affiche qu'aux superAdmin et l'API (/api/cards)
// est derrière requireSuperAdmin ; la page se contente d'un message si un
// membre de la team compta y arrive par l'URL.

import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useDateStore } from "../stores/date-store";
import { useAppSession } from "../lib/session";
import { apiGet } from "../lib/api";
import { formatCents, formatDateFr } from "../lib/format";
import { MonthlyBars, SERIES_COLORS, type CardSummary } from "../components/monthly-bars";

export const Route = createFileRoute("/cards")({ component: CardsPage });

interface CardComparison {
  period: { startDate: string; endDate: string };
  months: string[];
  cards: CardSummary[];
  untaggedCardTransactions: number;
}

function CardsPage() {
  const { isSuperAdmin } = useAppSession();
  const { startDate, endDate } = useDateStore();

  const { data, isPending, error } = useQuery<CardComparison>({
    queryKey: ["cards-compare", startDate, endDate],
    queryFn: () => apiGet<CardComparison>(`/api/cards/compare?${new URLSearchParams({ startDate, endDate })}`),
    enabled: isSuperAdmin,
  });

  if (!isSuperAdmin) {
    return <div className="rounded border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">Page réservée aux superAdmin.</div>;
  }
  if (isPending) {
    return <div className="flex h-64 items-center justify-center text-gray-500">Chargement...</div>;
  }
  if (error || !data) {
    return <div className="rounded border border-red-200 bg-red-50 p-4 text-sm text-red-700">Erreur : {(error as Error | null)?.message ?? "données indisponibles"}</div>;
  }

  const [first, second] = data.cards;
  const gapCents = first && second ? first.totalCents - second.totalCents : 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Cartes</h1>
        <p className="mt-1 text-sm text-gray-500">
          Dépenses des cartes Qonto du {formatDateFr(data.period.startDate)} au {formatDateFr(data.period.endDate)}, d'après les paiements carte réglés (remboursements déduits).
        </p>
      </div>

      {data.untaggedCardTransactions > 0 && (
        <div className="rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          {data.untaggedCardTransactions} paiement(s) carte de la période ont été fetchés avant que la carte soit enregistrée : lancez « Tout synchroniser » (page Sync) sur cette période pour les attribuer.
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-3">
        {data.cards.map((card, i) => (
          <StatTile key={card.id} color={SERIES_COLORS[i]} title={card.label} value={formatCents(card.totalCents)} hint={`${card.count} paiement${card.count > 1 ? "s" : ""}`} />
        ))}
        {first && second && (
          <StatTile
            title="Écart"
            value={formatCents(Math.abs(gapCents))}
            hint={gapCents === 0 ? "à égalité" : `${gapCents > 0 ? first.label : second.label} dépense plus`}
          />
        )}
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <h2 className="mb-3 text-sm font-semibold text-gray-800">Dépenses par mois</h2>
        <MonthlyBars months={data.months} cards={data.cards} />
      </div>

      {data.cards.map((card, i) => (
        <CardTransactions key={card.id} card={card} color={SERIES_COLORS[i]} />
      ))}
    </div>
  );
}

function StatTile({ title, value, hint, color }: { title: string; value: string; hint: string; color?: string }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-center gap-2 text-sm text-gray-500">
        {color && <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: color }} aria-hidden />}
        {title}
      </div>
      <div className="mt-1 text-2xl font-semibold tabular-nums text-gray-900">{value}</div>
      <div className="text-xs text-gray-500">{hint}</div>
    </div>
  );
}

function CardTransactions({ card, color }: { card: CardSummary; color: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
      <button type="button" onClick={() => setOpen((o) => !o)} className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-gray-50">
        <span className="w-4 text-gray-400">{open ? "▼" : "▶"}</span>
        <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: color }} aria-hidden />
        <span className="font-medium text-gray-900">{card.label}</span>
        <span className="text-sm text-gray-500">{card.count} paiement{card.count > 1 ? "s" : ""}</span>
        <span className="ml-auto tabular-nums font-semibold text-gray-900">{formatCents(card.totalCents)}</span>
      </button>
      {open && (
        <table className="w-full border-t border-gray-100 text-sm">
          <thead className="bg-gray-50 text-left text-xs text-gray-500">
            <tr>
              <th className="px-4 py-2">Date</th>
              <th className="px-4 py-2">Libellé</th>
              <th className="px-4 py-2">Catégorie</th>
              <th className="px-4 py-2 text-right">Montant</th>
            </tr>
          </thead>
          <tbody>
            {card.transactions.length === 0 && (
              <tr>
                <td colSpan={4} className="px-4 py-3 text-gray-400">Aucun paiement sur la période.</td>
              </tr>
            )}
            {card.transactions.map((t) => (
              <tr key={t.id} className="border-t border-gray-100">
                <td className="whitespace-nowrap px-4 py-2 text-gray-600">{formatDateFr(t.date)}</td>
                <td className="px-4 py-2 text-gray-900">{t.label}</td>
                <td className="px-4 py-2 text-gray-500">{t.category ?? "—"}</td>
                <td className={`whitespace-nowrap px-4 py-2 text-right tabular-nums ${t.amountCents < 0 ? "text-green-700" : "text-gray-900"}`}>{formatCents(t.amountCents)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
